import { Injectable } from '@angular/core';
import { EncryptionAlgorithm } from '../api';
import { OrderDirection } from './';

@Injectable({
  providedIn: 'root'
})
export class SharedService {
  toHexString(numbers: number[]): string {
    return numbers.map(x => x.toString(16).padStart(2, '0')).join(' ');
  }

  translateEncryptionAlgorithm(encryptionAlgorithm: EncryptionAlgorithm): string {
    if (encryptionAlgorithm === EncryptionAlgorithm.none) {
      return 'None';
    } else if (encryptionAlgorithm === EncryptionAlgorithm.aes128Rev) {
      return 'Aes128Rev';
    } else if (encryptionAlgorithm === EncryptionAlgorithm.aes128) {
      return 'Aes128';
    } else {
      return '' + encryptionAlgorithm;
    }
  }

  clone<T>(obj: T): T {
    return JSON.parse(JSON.stringify(obj));
  }

  escapeHTML(value: string): string {
    return ('' + value).replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  getFormattedDateTime(dateTime: string): string {
    return this.escapeHTML(dateTime);
  }

  isForbiddenError(err: any): boolean {
    if (!err) {
      return false;
    }
    return err.status === 403;
  }

  async getSha512HashHexString(text: string): Promise<string> {
    const utf8Bytes = new TextEncoder().encode(text);
    const sha512Buffer = await crypto.subtle.digest('SHA-512', utf8Bytes);
    const sha512Array = Array.from(new Uint8Array(sha512Buffer));
    const hashHex = sha512Array.map(b => b.toString(16).padStart(2, '0')).join('');
    return hashHex;
  }

  sortByPAIPAddress<T>(array: T[], paipValueSelector: (item: T) => string, orderDirection: OrderDirection = OrderDirection.descending): void {
    if (!array) {
      return;
    }
    if (array.length <= 1) {
      return;
    }

    array.sort((left, right) => {
      const leftPAIPAddr = paipValueSelector(left) || '';
      const rightPAIPAddr = paipValueSelector(right) || '';
      const leftPaddedString = leftPAIPAddr.split('.').map(x => x.padStart(4, '0')).join('.');
      const rightPaddedString = rightPAIPAddr.split('.').map(x => x.padStart(4, '0')).join('.');
      if (orderDirection === OrderDirection.ascending) {
        return leftPaddedString.localeCompare(rightPaddedString);
      } else {
        return rightPaddedString.localeCompare(leftPaddedString);
      }
    });
  }

  sort<T>(array: T[], valueSelector: (item: T) => string, orderDirection: OrderDirection = OrderDirection.ascending): void {
    if (!array) {
      return;
    }
    if (array.length <= 1) {
      return;
    }

    if (orderDirection === OrderDirection.ascending) {
      array.sort((left, right) =>
        (valueSelector(left) || '').toLowerCase().localeCompare((valueSelector(right) || '').toLowerCase())
      );
    } else if (orderDirection === OrderDirection.descending) {
      array.sort((left, right) =>
        (valueSelector(right) || '').toLowerCase().localeCompare((valueSelector(left) || '').toLowerCase())
      );
    }
  }

  sortByNumber<T>(array: T[], valueSelector: (item: T) => number, orderDirection: OrderDirection = OrderDirection.ascending): void {
    if (!array) {
      return;
    }
    if (array.length <= 1) {
      return;
    }

    if (orderDirection === OrderDirection.ascending) {
      array.sort((left, right) => Math.sign(valueSelector(left) - valueSelector(right)));
    } else if (orderDirection === OrderDirection.descending) {
      array.sort((left, right) => Math.sign(valueSelector(right) - valueSelector(left)));
    }
  }

  commonSort<T>(array: T[], key: keyof T, orderDirection: OrderDirection = OrderDirection.ascending): void {
    if (!array || array.length <= 1) return;
    array.sort((a: any, b: any) => {
      let valA = a[key];
      let valB = b[key];
      if (valA == null) valA = '';
      if (valB == null) valB = '';
      // Number
      if (!isNaN(Number(valA)) && !isNaN(Number(valB))) {
        return (Number(valA) - Number(valB)) * (orderDirection === OrderDirection.ascending ? 1 : -1);
      }
      // Date
      const dateA = new Date(valA).getTime();
      const dateB = new Date(valB).getTime();
      if (!isNaN(dateA) && !isNaN(dateB)) {
        return (dateA - dateB) * (orderDirection === OrderDirection.ascending ? 1 : -1);
      }
      // String fallback
      return (String(valA) || '')
        .toLowerCase()
        .localeCompare((String(valB) || '').toLowerCase()) *
        (orderDirection === OrderDirection.ascending ? 1 : -1);
    });
  }

}
